import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../utils/api";
import { Validation } from "../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import { Member } from "../../../../@types/member.types";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../utils/commonFunctions/date";

import { useEmployerRoute } from "../../../../hooks";
import { Group } from "../../../../@types/group.types";
import useRouteParam from "../../../../hooks/useRouteParam";
import ROLES from "../../../../constants/roles";
import { hireDateUpdatePayloadType } from "../../../../utils/api/memberInfoUpdate/hireDateUpdate";

export type HireDateTypes = {
	group_number: number | null;
	role: string;
	SSN: string;
	hire_date: string;
};

const chipMessage = "This functionality will update the Hire Date for Employee of specific group.";

const HireDateUpdate = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<HireDateTypes>({
		group_number: null,
		role: "",
		SSN: "",
		hire_date: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, hire_date, role, SSN } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (role.length === 0) {
			_validation.current.reportFieldDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (SSN.length === 0) {
			_validation.current.reportFieldDetails["SSN"] = "SSN is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (hire_date.length === 0) {
			_validation.current.reportFieldDetails["hire_date"] = "Hire date is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
		},
		[reportFieldDetails]
	);

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Hire Date Update");
					const { group_number, role, SSN, hire_date } = reportFieldDetails;
					const hireDate = new Date(hire_date);

					const hireDatePayload: any = {
						group_number,
						role,
						SSN: SSN.replaceAll("-", ""),
						hire_date: hireDate.getFullYear() + "-" + (hireDate.getMonth() + 1) + "-" + hireDate.getDate()
					};

					// console.log("Hire Date Payload", hireDatePayload);

					const response: any = await trackPromise(api.memberInfoUpdate.hireDateUpdate(hireDatePayload));

					// console.log("Response", response);
					if (response) {
						// console.log("Response", response);
						alert("Hire Date Updation Successful");
						window.location.reload();

						// setTimeout(() => {
						// 	setSnackbarAPICallProps(
						// 		Object.assign({}, snackbarAPICallProps, {
						// 			open: true,
						// 			message: "Employee Info Changes Report Fetched Successfully and File Downloaded!",
						// 			severity: "success"
						// 		})
						// 	);
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Hire Date Update Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.hire_date_updation);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.hire_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { hire_date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Hire Date Update Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Employee Role",
							name: "role",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.role,
							type: "select",
							options: [ROLES.employee, ROLES.dependent]
						},
						{
							label: "Member SSN",
							name: "SSN",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Member SSN",
							value: reportFieldDetails.SSN,
							type: "textfield"
						},
						{
							label: "Hire Date",
							name: "hire_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter hire date",
							value: reportFieldDetails.hire_date,
							type: "date"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleDateChange,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default HireDateUpdate;
